import { defineMessages } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

export const readonlyTitleMessages: Record<string, MessageDescriptor> = defineMessages({
	updateWorkItemFields: {
		id: 'conversation-assistant-message-actions.readonly-title.update-work-item-fields',
		defaultMessage: 'Update work item',
		description: 'Read-only action card title for updating one or more fields on a Jira work item.',
	},
	createProjectProgressUpdate: {
		id: 'conversation-assistant-message-actions.readonly-title.create-project-progress-update',
		defaultMessage: 'Create project update',
		description: 'Read-only action card title for creating an Atlassian project progress update.',
	},
	updateWorkItems: {
		id: 'conversation-assistant-message-actions.readonly-title.update-work-items',
		defaultMessage: 'Update work items',
		description: 'Read-only action card title for updating multiple Jira work items.',
	},
	findWorkItems: {
		id: 'conversation-assistant-message-actions.readonly-title.find-work-items',
		defaultMessage: 'Find work items',
		description: 'Read-only action card title for finding Jira work items.',
	},
	createWorkItem: {
		id: 'conversation-assistant-message-actions.readonly-title.create-work-item',
		defaultMessage: 'Create work item',
		description: 'Read-only action card title for creating a Jira work item.',
	},
	jiraAdminAction: {
		id: 'conversation-assistant-message-actions.readonly-title.jira-admin-action',
		defaultMessage: 'Jira admin action',
		description: 'Read-only action card title for a Jira admin action.',
	},
	transitionWorkItem: {
		id: 'conversation-assistant-message-actions.readonly-title.transition-work-item',
		defaultMessage: 'Transition work item',
		description: 'Read-only action card title for transitioning a Jira work item.',
	},
	suggestEpics: {
		id: 'conversation-assistant-message-actions.readonly-title.suggest-epics',
		defaultMessage: 'Suggest epics',
		description: 'Read-only action card title for suggesting Jira epics.',
	},
	deleteWorkItem: {
		id: 'conversation-assistant-message-actions.readonly-title.delete-work-item',
		defaultMessage: 'Delete work item',
		description: 'Read-only action card title for deleting a Jira work item.',
	},
	moveWorkItem: {
		id: 'conversation-assistant-message-actions.readonly-title.move-work-item',
		defaultMessage: 'Move work item',
		description: 'Read-only action card title for moving a Jira work item.',
	},
	assignWorkItem: {
		id: 'conversation-assistant-message-actions.readonly-title.assign-work-item',
		defaultMessage: 'Assign work item',
		description: 'Read-only action card title for assigning a Jira work item.',
	},
	createRelease: {
		id: 'conversation-assistant-message-actions.readonly-title.create-release',
		defaultMessage: 'Create release',
		description: 'Read-only action card title for creating a Jira release.',
	},
	createView: {
		id: 'conversation-assistant-message-actions.readonly-title.create-view',
		defaultMessage: 'Create view',
		description: 'Read-only action card title for creating and configuring a Jira view.',
	},
	createSprint: {
		id: 'conversation-assistant-message-actions.readonly-title.create-sprint',
		defaultMessage: 'Create sprint',
		description: 'Read-only action card title for creating a Jira sprint.',
	},
	startSprint: {
		id: 'conversation-assistant-message-actions.readonly-title.start-sprint',
		defaultMessage: 'Start sprint',
		description: 'Read-only action card title for starting a Jira sprint.',
	},
	moveToSprint: {
		id: 'conversation-assistant-message-actions.readonly-title.move-to-sprint',
		defaultMessage: 'Move to sprint',
		description: 'Read-only action card title for moving Jira work items to a sprint.',
	},
	manageCapacityPlan: {
		id: 'conversation-assistant-message-actions.readonly-title.manage-capacity-plan',
		defaultMessage: 'Manage capacity plan',
		description: 'Read-only action card title for managing a capacity plan.',
	},
	updateWorkflow: {
		id: 'conversation-assistant-message-actions.readonly-title.update-workflow',
		defaultMessage: 'Update workflow',
		description: 'Read-only action card title for updating a Jira workflow.',
	},
	createPage: {
		id: 'conversation-assistant-message-actions.readonly-title.create-page',
		defaultMessage: 'Create page',
		description: 'Read-only action card title for creating a Confluence page.',
	},
	createFolder: {
		id: 'conversation-assistant-message-actions.readonly-title.create-folder',
		defaultMessage: 'Create folder',
		description: 'Read-only action card title for creating a Confluence folder.',
	},
	commentOnPage: {
		id: 'conversation-assistant-message-actions.readonly-title.comment-on-page',
		defaultMessage: 'Comment on page',
		description: 'Read-only action card title for commenting on a Confluence page.',
	},
	editContent: {
		id: 'conversation-assistant-message-actions.readonly-title.edit-content',
		defaultMessage: 'Edit content',
		description: 'Read-only action card title for editing Confluence content.',
	},
	addLabel: {
		id: 'conversation-assistant-message-actions.readonly-title.add-label',
		defaultMessage: 'Add label',
		description: 'Read-only action card title for adding a label to a Confluence page.',
	},
	renamePage: {
		id: 'conversation-assistant-message-actions.readonly-title.rename-page',
		defaultMessage: 'Rename page',
		description: 'Read-only action card title for renaming a Confluence page.',
	},
	movePage: {
		id: 'conversation-assistant-message-actions.readonly-title.move-page',
		defaultMessage: 'Move page',
		description: 'Read-only action card title for moving a Confluence page.',
	},
	createWhiteboard: {
		id: 'conversation-assistant-message-actions.readonly-title.create-whiteboard',
		defaultMessage: 'Create whiteboard',
		description: 'Read-only action card title for creating a Confluence whiteboard.',
	},
	updateWhiteboard: {
		id: 'conversation-assistant-message-actions.readonly-title.update-whiteboard',
		defaultMessage: 'Update whiteboard',
		description: 'Read-only action card title for updating a Confluence whiteboard.',
	},
	createSlides: {
		id: 'conversation-assistant-message-actions.readonly-title.create-slides',
		defaultMessage: 'Create slides',
		description: 'Read-only action card title for creating Confluence slides.',
	},
	updateSlides: {
		id: 'conversation-assistant-message-actions.readonly-title.update-slides',
		defaultMessage: 'Update slides',
		description: 'Read-only action card title for updating Confluence slides.',
	},
	createDatabase: {
		id: 'conversation-assistant-message-actions.readonly-title.create-database',
		defaultMessage: 'Create database',
		description: 'Read-only action card title for creating a Confluence database.',
	},
	updateDatabase: {
		id: 'conversation-assistant-message-actions.readonly-title.update-database',
		defaultMessage: 'Update database',
		description: 'Read-only action card title for updating a Confluence database.',
	},
	createSpace: {
		id: 'conversation-assistant-message-actions.readonly-title.create-space',
		defaultMessage: 'Create space',
		description: 'Read-only action card title for creating a Confluence space.',
	},
	updateSpace: {
		id: 'conversation-assistant-message-actions.readonly-title.update-space',
		defaultMessage: 'Update space',
		description: 'Read-only action card title for updating a Confluence space.',
	},
	createDynamicUi: {
		id: 'conversation-assistant-message-actions.readonly-title.create-dynamic-ui',
		defaultMessage: 'Create app',
		description: 'Read-only action card title for creating a dynamic app UI.',
	},
	createImage: {
		id: 'conversation-assistant-message-actions.readonly-title.create-image',
		defaultMessage: 'Create image',
		description: 'Read-only action card title for creating an image.',
	},
	createRequest: {
		id: 'conversation-assistant-message-actions.readonly-title.create-request',
		defaultMessage: 'Create request',
		description: 'Read-only action card title for creating a Jira Service Management request.',
	},
	createSurvey: {
		id: 'conversation-assistant-message-actions.readonly-title.create-survey',
		defaultMessage: 'Create survey',
		description: 'Read-only action card title for creating a Jira Service Management survey.',
	},
	createQueue: {
		id: 'conversation-assistant-message-actions.readonly-title.create-queue',
		defaultMessage: 'Create queue',
		description: 'Read-only action card title for creating a Jira Service Management queue.',
	},
	createQueueGroup: {
		id: 'conversation-assistant-message-actions.readonly-title.create-queue-group',
		defaultMessage: 'Create queue group',
		description: 'Read-only action card title for creating a Jira Service Management queue group.',
	},
	updateQueue: {
		id: 'conversation-assistant-message-actions.readonly-title.update-queue',
		defaultMessage: 'Update queue',
		description: 'Read-only action card title for updating a Jira Service Management queue.',
	},
	updateQueueGroup: {
		id: 'conversation-assistant-message-actions.readonly-title.update-queue-group',
		defaultMessage: 'Update queue group',
		description: 'Read-only action card title for updating a Jira Service Management queue group.',
	},
	createRequestType: {
		id: 'conversation-assistant-message-actions.readonly-title.create-request-type',
		defaultMessage: 'Create request type',
		description: 'Read-only action card title for creating a Jira Service Management request type.',
	},
	updateRequestType: {
		id: 'conversation-assistant-message-actions.readonly-title.update-request-type',
		defaultMessage: 'Update request type',
		description: 'Read-only action card title for updating a Jira Service Management request type.',
	},
	updatePortalGroups: {
		id: 'conversation-assistant-message-actions.readonly-title.update-portal-groups',
		defaultMessage: 'Update portal groups',
		description: 'Read-only action card title for updating Jira Service Management portal groups.',
	},
	updateRequestTypeWorkflow: {
		id: 'conversation-assistant-message-actions.readonly-title.update-request-type-workflow',
		defaultMessage: 'Update request type workflow',
		description:
			'Read-only action card title for updating a Jira Service Management request type workflow.',
	},
	selectHardwareTypes: {
		id: 'conversation-assistant-message-actions.readonly-title.select-hardware-types',
		defaultMessage: 'Select hardware types',
		description: 'Read-only action card title for selecting hardware asset types.',
	},
	completeSetup: {
		id: 'conversation-assistant-message-actions.readonly-title.complete-setup',
		defaultMessage: 'Complete setup',
		description: 'Read-only action card title for completing a setup flow.',
	},
	createKnowledgeBaseContent: {
		id: 'conversation-assistant-message-actions.readonly-title.create-knowledge-base-content',
		defaultMessage: 'Create knowledge base content',
		description:
			'Read-only action card title for creating Jira Service Management knowledge base content.',
	},
	createDashboard: {
		id: 'conversation-assistant-message-actions.readonly-title.create-dashboard',
		defaultMessage: 'Create dashboard',
		description: 'Read-only action card title for creating an Analytics dashboard.',
	},
	createChart: {
		id: 'conversation-assistant-message-actions.readonly-title.create-chart',
		defaultMessage: 'Create chart',
		description: 'Read-only action card title for creating an Analytics chart.',
	},
	createFilterExpression: {
		id: 'conversation-assistant-message-actions.readonly-title.create-filter-expression',
		defaultMessage: 'Create filter expression',
		description: 'Read-only action card title for creating an Analytics filter expression.',
	},
	deepResearchReport: {
		id: 'conversation-assistant-message-actions.readonly-title.deep-research-report',
		defaultMessage: 'Create report',
		description: 'Read-only action card title for creating a deep research report.',
	},
	buildSolution: {
		id: 'conversation-assistant-message-actions.readonly-title.build-solution',
		defaultMessage: 'Build solution',
		description: 'Read-only action card title for building a Studio solution.',
	},
	createAutomationRule: {
		id: 'conversation-assistant-message-actions.readonly-title.create-automation-rule',
		defaultMessage: 'Create automation rule',
		description: 'Read-only action card title for creating an automation rule.',
	},
	respondToPrompt: {
		id: 'conversation-assistant-message-actions.readonly-title.respond-to-prompt',
		defaultMessage: 'Respond to prompt',
		description: 'Read-only action card title for responding to an information prompt.',
	},
	reviewChoices: {
		id: 'conversation-assistant-message-actions.readonly-title.review-choices',
		defaultMessage: 'Review choices',
		description: 'Read-only action card title for reviewing multiple-choice options.',
	},
	hiringManagerAction: {
		id: 'conversation-assistant-message-actions.readonly-title.hiring-manager-action',
		defaultMessage: 'Hiring manager action',
		description: 'Read-only action card title for a hiring manager action.',
	},
	updateAgentDraft: {
		id: 'conversation-assistant-message-actions.readonly-title.update-agent-draft',
		defaultMessage: 'Update agent draft',
		description: 'Read-only action card title for updating an agent draft.',
	},
	updateAgentConfiguration: {
		id: 'conversation-assistant-message-actions.readonly-title.update-agent-configuration',
		defaultMessage: 'Update agent configuration',
		description: 'Read-only action card title for updating an agent configuration.',
	},
	createAgent: {
		id: 'conversation-assistant-message-actions.readonly-title.create-agent',
		defaultMessage: 'Create agent',
		description: 'Read-only action card title for creating an agent.',
	},
	openStudio: {
		id: 'conversation-assistant-message-actions.readonly-title.open-studio',
		defaultMessage: 'Open Studio',
		description: 'Read-only action card title for opening Studio.',
	},
	viewRecommendation: {
		id: 'conversation-assistant-message-actions.readonly-title.view-recommendation',
		defaultMessage: 'View recommendation',
		description: 'Read-only action card title for viewing an app recommendation.',
	},
	archiveTeam: {
		id: 'conversation-assistant-message-actions.readonly-title.archive-team',
		defaultMessage: 'Archive team',
		description: 'Read-only action card title for archiving an Atlassian team.',
	},
	createAsks: {
		id: 'conversation-assistant-message-actions.readonly-title.create-asks',
		defaultMessage: 'Create asks',
		description: 'Read-only action card title for creating Focus asks.',
	},
	scheduleChat: {
		id: 'conversation-assistant-message-actions.readonly-title.schedule-chat',
		defaultMessage: 'Schedule chat',
		description: 'Read-only action card title for scheduling a chat.',
	},
	completePlan: {
		id: 'conversation-assistant-message-actions.readonly-title.complete-plan',
		defaultMessage: 'Complete plan',
		description: 'Read-only action card title for completing a plan.',
	},
	askQuestion: {
		id: 'conversation-assistant-message-actions.readonly-title.ask-question',
		defaultMessage: 'Ask question',
		description: 'Read-only action card title for asking a question.',
	},
	searchImages: {
		id: 'conversation-assistant-message-actions.readonly-title.search-images',
		defaultMessage: 'Search images',
		description: 'Read-only action card title for searching images.',
	},
	sendSlackMessage: {
		id: 'conversation-assistant-message-actions.readonly-title.send-slack-message',
		defaultMessage: 'Send Slack message',
		description: 'Read-only action card title for sending a Slack message.',
	},
	commentOnPullRequest: {
		id: 'conversation-assistant-message-actions.readonly-title.comment-on-pull-request',
		defaultMessage: 'Comment on pull request',
		description: 'Read-only action card title for commenting on a Bitbucket pull request.',
	},
	createCustomSkill: {
		id: 'conversation-assistant-message-actions.readonly-title.create-custom-skill',
		defaultMessage: 'Create custom skill',
		description: 'Read-only action card title for creating a custom skill.',
	},
	updateCustomSkill: {
		id: 'conversation-assistant-message-actions.readonly-title.update-custom-skill',
		defaultMessage: 'Update custom skill',
		description: 'Read-only action card title for updating a custom skill.',
	},
	createCalendarEvent: {
		id: 'conversation-assistant-message-actions.readonly-title.create-calendar-event',
		defaultMessage: 'Create calendar event',
		description: 'Read-only action card title for creating a Google Calendar event.',
	},
	createDocument: {
		id: 'conversation-assistant-message-actions.readonly-title.create-document',
		defaultMessage: 'Create document',
		description: 'Read-only action card title for creating a Google document.',
	},
	actionUnavailable: {
		id: 'conversation-assistant-message-actions.readonly-title.action-unavailable',
		defaultMessage: 'Action unavailable',
		description: 'Fallback read-only action card title when no local action title is registered.',
	},
});
